import type { ApiClient } from '@center-technologies/shared-api-clients';
import { CATALOG } from '@center-platform/component-catalog';
import type { A2uiComponentEntry } from 'types/space';
import type { CompositionDefinition, CompositionRef } from 'types/composition';
import {
  CompositionResolutionUnavailableError,
  httpStatusOf,
  isNotFoundStatus,
  type ArtifactCompositionResolver,
  type CompositionRefResolution,
} from '@center-platform/space-format/conformance';

interface CompositionApiResult {
  version: number | null;
  definition: CompositionDefinition | null;
}

export function isCatalogPrimitive(systemName: string): boolean {
  return CATALOG.some((entry) => entry.type === systemName);
}

/** Composition refs reachable one level down from `components` — excludes catalog primitives. */
function collectRefs(components: readonly A2uiComponentEntry[]): CompositionRef[] {
  const refs: CompositionRef[] = [];
  for (const entry of components) {
    if (!isCatalogPrimitive(entry.component)) {
      refs.push({ systemName: entry.component, version: entry.componentVersion as number | undefined });
    }
  }
  return refs;
}

function refKey(ref: CompositionRef): string {
  return `${ref.systemName}@${ref.version ?? 'latest'}`;
}

function cacheKey(systemName: string, version: number): string {
  return `${systemName}@${version}`;
}

/** Safety cap on BFS rounds — mirrors composer-agent-service's `apiCompositionResolver.ts` and schema-service's `dbCompositionResolver.ts`: a legitimate composition graph resolves in a handful of rounds; this guards against a pathological/cyclic case looping forever rather than failing closed via `verifyArtifact`'s own cycle detection. */
const MAX_WARM_ROUNDS = 20;

export interface ClientCompositionCache {
  byVersion: Map<string, { version: number; definition: CompositionDefinition }>;
  latestVersion: Map<string, number>;
}

/**
 * Loads every composition transitively reachable from `components`, over the browser's
 * `ApiClient` — the composer check panel's resolver for `verifyArtifact`, the same
 * warm-a-cache-then-hand-back-a-synchronous-resolver shape as `dbCompositionResolver.ts`
 * (schema-service) and `apiCompositionResolver.ts` (composer-agent-service). Deliberately NOT
 * `useCompositionResolver.ts`'s session-long published-version cache: that hook is a RENDER-path
 * resolver (`CompositionResolver`, `CompositionDefinition | undefined`) built for a mounted Space
 * instance, and pre-lockfile it only sees top-level refs — this check runs on-demand, needs the
 * richer 3-state `CompositionRefResolution` `verifyArtifact` requires, and must see transitively
 * nested refs even for an unpublished, unlocked draft.
 *
 * A 404 leaves the ref absent from the cache — the resolver then reports `unresolved`, which is
 * exactly what `REF_UNRESOLVED` means. Any OTHER failure (5xx, network) throws
 * `CompositionResolutionUnavailableError`: painting the check panel red because the backend
 * hiccuped would tell the author their document is broken when it is not.
 */
export async function loadClientCompositionCache(
  apiClient: ApiClient,
  a2ui: { components: readonly A2uiComponentEntry[] }
): Promise<ClientCompositionCache> {
  const cache: ClientCompositionCache = { byVersion: new Map(), latestVersion: new Map() };
  const seen = new Set<string>();
  let frontier = new Map(collectRefs(a2ui.components).map((ref) => [refKey(ref), ref]));

  for (let round = 0; round < MAX_WARM_ROUNDS && frontier.size > 0; round += 1) {
    const refs = [...frontier.values()].filter((ref) => !seen.has(refKey(ref)));
    refs.forEach((ref) => seen.add(refKey(ref)));
    if (refs.length === 0) break;

    const results = await Promise.all(
      refs.map(async (ref) => {
        try {
          const versionQuery = ref.version === undefined ? '' : `?version=${ref.version}`;
          const data = await apiClient.get<{ composition: CompositionApiResult }>(
            `/compositions/${encodeURIComponent(ref.systemName)}${versionQuery}`
          );
          return { ref, result: data?.composition ?? null };
        } catch (err) {
          const status = httpStatusOf(err);
          if (isNotFoundStatus(status)) return { ref, result: null };
          throw new CompositionResolutionUnavailableError(ref.systemName, { version: ref.version, status, cause: err });
        }
      })
    );

    const nextFrontier = new Map<string, CompositionRef>();
    for (const { ref, result } of results) {
      if (!result?.definition || result.version === null) continue;
      cache.byVersion.set(cacheKey(ref.systemName, result.version), { version: result.version, definition: result.definition });
      if (ref.version === undefined) {
        cache.latestVersion.set(ref.systemName, result.version);
      }
      for (const nextRef of collectRefs(result.definition.a2ui.components)) {
        if (!seen.has(refKey(nextRef))) nextFrontier.set(refKey(nextRef), nextRef);
      }
    }
    frontier = nextFrontier;
  }

  return cache;
}

function resolveRow(cache: ClientCompositionCache, ref: CompositionRef) {
  const version = ref.version ?? cache.latestVersion.get(ref.systemName);
  if (version === undefined) return undefined;
  return cache.byVersion.get(cacheKey(ref.systemName, version));
}

/** Production `ArtifactCompositionResolver` for the composer check panel — synchronous per `verifyArtifact`'s contract, backed by a cache warmed once via `loadClientCompositionCache`. */
export function createClientCompositionResolver(cache: ClientCompositionCache): ArtifactCompositionResolver {
  return (ref: CompositionRef): CompositionRefResolution => {
    if (isCatalogPrimitive(ref.systemName)) return { status: 'primitive' };
    const row = resolveRow(cache, ref);
    if (!row) return { status: 'unresolved' };
    return { status: 'resolved', version: row.version, definition: row.definition };
  };
}
